import React from 'react';
import GrassOutlinedIcon from '@mui/icons-material/GrassOutlined';
import { curingColour } from '../api/curing';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  CardActionArea,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import { format } from 'date-fns';
import { Project } from '../types/project';
import { describeLoadingLayers } from './projectListing/projectStatus';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useArchiveProject, usePinProject } from '../api/projects';
import { useProjectConfig } from '../contexts/ProjectConfigContext';
import { useAuth } from '../contexts/AuthContext';
import { isViewOnly } from '../utils/permissions';

// Mapbox access token
mapboxgl.accessToken = "pk.eyJ1IjoiYnVwcG9vODUiLCJhIjoiY2xzaGV6M2d6MjV4ajJudDAwOXo1bjMwcSJ9.0LeBUIZbR4UWWloslEZjlA";

type ProjectCardProps = {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const {
    mutate: archiveProject,
    isPending: isArchivePending,
    isSuccess: isArchiveDone,
  } = useArchiveProject();
  const { mutate: setPinned } = usePinProject();
  const navigate = useNavigate();
  const { openConfigModal } = useProjectConfig();
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const isPinned = !!project.is_pinned;
  const openIncidents = project.open_incident_count ?? 0;

  const isProcessing = project.status === 'processing' && !!project.task_id;
  const isExpanding = project.status === 'expanding' && !!project.task_id;
  const isRefreshing = project.status === 'refreshing';
  // Initialization failed — the project has no usable layers, so the card is
  // unloadable; only archiving remains available (#184).
  const isFailed = project.status === 'failed';
  // Keep the archiving state through the post-success list refetch so the
  // card stays visibly "archiving" until it unmounts.
  const isArchiving = isArchivePending || isArchiveDone;

  function handleClick() {
    if (isProcessing || isArchiving || isFailed) return;
    navigate(`/projects/${project.id}`);
  }

  function handleTagConfigClick(e: React.MouseEvent) {
    e.stopPropagation();
    openConfigModal(3, project.id);
  }

  function handlePinClick(e: React.MouseEvent) {
    e.stopPropagation();
    setPinned({ projectId: String(project.id), pinned: !isPinned });
  }

  return (
    <Card onClick={handleClick}
          sx={{
            width: 360,
            height: 360,
            position: 'relative',
            cursor: isProcessing || isArchiving || isFailed ? 'default' : 'pointer',
            // Edge colour by urgency: failed red, open incidents amber (with
            // a soft glow so it reads across a wall of cards), pinned teal.
            border: isFailed
              ? '1px solid #fca5a5'
              : openIncidents > 0
                ? '2px solid #f59e0b'
                : isPinned
                  ? '1px solid #00d4ad'
                  : undefined,
            boxShadow:
              openIncidents > 0 && !isFailed
                ? '0 0 10px rgba(245, 158, 11, 0.45)'
                : undefined,
          }}>
      <CardActionArea sx={{ height: '100%', opacity: isProcessing || isArchiving || isFailed ? 0.6 : 1 }} disabled={isProcessing || isArchiving || isFailed}>
        <CardContent style={{ height: '100%' }}>
          <div style={{ height:200, width:'100%', marginBottom: 8 }}>
            <img
              src={`${project.map_screenshot_url}?access_token=${mapboxgl.accessToken}`}
              alt="Map screenshot"
              height={200}
              width={336}
              style={{
                filter: isProcessing || isFailed ? 'grayscale(1)' : 'none',
                opacity: isProcessing || isFailed ? 0.7 : 1,
              }}
            />
          </div>
          <Typography noWrap component="div" sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: 600 }}>
            {project.name}
          </Typography>
          {isArchiving ? (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={12} />
              Archiving…
            </Typography>
          ) : isFailed ? (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#ef4444', fontWeight: 600 }}>
              <ErrorOutlineIcon sx={{ fontSize: 16 }} />
              Initialization failed
            </Typography>
          ) : isProcessing ? (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={12} />
              {project.init_tiles_total && project.init_tiles_total > 1
                ? `Initializing ${project.init_tiles_completed ?? 0} of ${project.init_tiles_total} tiles…`
                : 'Initializing…'}
            </Typography>
          ) : isExpanding ? (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={12} />
              Expanding Project…
            </Typography>
          ) : describeLoadingLayers(project) ? (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#15803d' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
              Ready to open
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                · {describeLoadingLayers(project)?.replace(/…$/, '')} in background
              </Typography>
            </Typography>
          ) : isRefreshing ? (
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={12} />
              Refreshing data…
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Updated: {format(new Date(project.updated), 'MMM d, yyyy h:mma')}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            By: {project.username}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <HandymanOutlinedIcon fontSize="small" sx={{ color: '#f59e0b' }} />
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                {isProcessing || isFailed ? '—' : (project.strategy_count ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Strategies</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LocalFireDepartmentOutlinedIcon fontSize="small" sx={{ color: '#ef4444' }} />
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                {isProcessing || isFailed ? '—' : (project.fire_simulation_count ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Fire sims</Typography>
            </Box>
            {project.curing_summary && (
              <Tooltip
                title={`Grass curing measured from satellite imagery on ${project.curing_summary.estimate_date}`}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <GrassOutlinedIcon
                    fontSize="small"
                    sx={{ color: curingColour(project.curing_summary.mean_pct) }}
                  />
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                    {Math.round(project.curing_summary.mean_pct)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Curing</Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </CardContent>
        {isProcessing && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {isFailed && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Chip
              icon={<ErrorOutlineIcon />}
              label="Initialization failed"
              color="error"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}
      </CardActionArea>
      {/* Open detection incidents: amber chip over the thumbnail, top-left;
          clicking opens the project map, where the Incidents layer shows
          them in place (the Incidents tab lists them too). */}
      {openIncidents > 0 && !isFailed && (
        <Tooltip title="View incidents on the map">
          <Chip
            icon={<LocalFireDepartmentOutlinedIcon sx={{ fill: '#fff' }} />}
            label={`${openIncidents} open incident${openIncidents === 1 ? '' : 's'}`}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${project.id}`);
            }}
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: 1,
              backgroundColor: '#f59e0b',
              color: '#fff',
              fontWeight: 600,
              '& .MuiChip-icon': { color: '#fff' },
              '&:hover': { backgroundColor: '#d97706' },
            }}
          />
        </Tooltip>
      )}
      {/* Pin toggle sits over the map thumbnail, top-right. View-only users
          still see the pinned state — a pin re-orders the listing for the
          whole tenant, so hiding it entirely would leave them wondering why
          some cards jumped the queue — but can't change it. */}
      {(!viewOnly || isPinned) && (
      <Tooltip title={viewOnly ? 'Pinned to the top for everyone' : isPinned ? 'Unpin from top' : 'Pin to top for everyone'}>
        <span style={{ position: 'absolute', right: 6, top: 6, zIndex: 1 }}>
          <IconButton
            onClick={handlePinClick}
            size="small"
            disabled={viewOnly || isArchiving}
            aria-label={isPinned ? 'Unpin project' : 'Pin project'}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: isPinned ? '#00d4ad' : '#6b7280',
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
              // Disabled only means "not yours to change" here, so keep the
              // pinned state at full contrast rather than greying it out.
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: isPinned ? '#00d4ad' : '#9ca3af',
              },
            }}
          >
            {isPinned ? (
              <PushPinIcon fontSize="small" />
            ) : (
              <PushPinOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, position: 'absolute', bottom: 10, left: 10, alignItems: 'center' }}>
        {project.tags && project.tags.length > 0 && (
          <>
            {project.tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: '#e0f2fe',
                  color: '#0369a1',
                }}
              />
            ))}
            {project.tags.length > 3 && (
              <Chip
                label={`+${project.tags.length - 3}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                }}
              />
            )}
          </>
        )}
        {!viewOnly && (
        <Tooltip title="Add or manage tags">
          <IconButton
            size="small"
            onClick={handleTagConfigClick}
            sx={{
              height: 20,
              width: 20,
              padding: 0,
              backgroundColor: '#00d4ad',
              color: 'white',
              '&:hover': {
                backgroundColor: '#00b496',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '0.875rem',
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
        )}
      </Box>
      {!viewOnly && (
      <Tooltip title={isArchiving ? 'Archiving…' : 'Archive project'}>
        {/* span keeps the tooltip working while the button is disabled */}
        <span style={{ position: 'absolute', right: 6, bottom: 6, zIndex: 1 }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (isArchiving) return;
              archiveProject(String(project.id));
            }}
            size="small"
            disabled={isArchiving}
            sx={{ color: '#00d4ad', cursor: 'pointer' }}
          >
            {isArchiving ? (
              <CircularProgress size={18} sx={{ color: '#00d4ad' }} />
            ) : (
              <ArchiveOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      )}
    </Card>
    );
  };

export default ProjectCard;
